import React, { useState } from "react";
import { Box, Container, Fab, TableCell } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { CTable, Text, HFlex, VFlex, CDialog, ErrorBox, Loading } from "shared";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "utils/hooks";
import { Country, countries } from "utils/market";
import { useSearchHealthCareOrganizations, HcoListItem } from "./queries/useHealthCareOrganizations";
import { HealthCareOrganizationForm } from "./components/HealthCareOrganizationForm";
import SearchHcoForm, { HcoSearchCategory as Category } from "./components/SearchHcoForm";
import { RowLink } from "shared/CTable/CTable";
import { Area } from "types";
import { join } from "utils/misc";

const Cell: React.VFC<{ children?: React.ReactNode }> = ({ children }) => (
  <TableCell>
    <Text tiny>{children}</Text>
  </TableCell>
);

const HealthCareOrganizations: React.VFC = () => {
  const { t } = useTranslation();
  const [isDialogOpen, setDialogOpen] = useState(false);

  const searchQuery = useSearchParams(
    {
      country: countries[0].value,
      category: Category.id,
      search: "",
    },
    {
      country: Object.values(Country),
      category: Object.values(Category),
    }
  );

  const {
    data: result,
    isLoading,
    error,
  } = useSearchHealthCareOrganizations({
    country: searchQuery.country,
    category: searchQuery.category as Category,
    search: searchQuery.search,
  });

  const openCreateDialog = () => {
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const tableHeader = {
    id: t("patients.id"),
    name: t("form.name"),
  };

  const tableCellFactory = (item: HcoListItem) => ({
    id: (
      <Cell>
        <RowLink
          to={{
            pathname: join(Area.HealthCareOrganizations, item.id),
            state: window.location.href.match(/\?.+$/)?.[0],
          }}
        />
        <Text tiny>{item.id}</Text>
      </Cell>
    ),
    name: <Cell>{item.name}</Cell>,
  });

  const noMatchEm = (
    <Text bold secondary paragraph mb={0} center>
      {t("dashboard.tables.no_hcos_found")}
    </Text>
  );

  const tableEm = (
    <Container>
      <CTable header={tableHeader} items={result?.items} cellFactory={tableCellFactory} />
    </Container>
  );

  const isIdle = !searchQuery.search;
  const isError = !!error;
  const isSuccess = !isLoading && !isError && !!result;

  const resultEm = (
    <Box bgcolor="background.paper" py={2} overflow="auto" data-testid="search-results">
      {isError && <ErrorBox error={error!} verbose centered dark />}
      {isLoading && <Loading />}
      {isSuccess && (result?.items?.length ? tableEm : noMatchEm)}
    </Box>
  );

  return (
    <VFlex>
      <HFlex alignSelf="flex-end" mr={3} my={3}>
        <Box display="inline-flex" alignItems="center">
          <Text caption secondary mr={1}>
            {t("buttons.create")}
          </Text>
          <Fab color="secondary" size="medium" onClick={openCreateDialog} aria-label="Create HCO">
            <Add />
          </Fab>
        </Box>
      </HFlex>
      <Container maxWidth="md">
        <Box mb={3}>
          <SearchHcoForm {...searchQuery} />
        </Box>
      </Container>
      {!isIdle && resultEm}

      <CDialog maxWidth="sm" fullWidth open={isDialogOpen} onClose={closeDialog}>
        <HealthCareOrganizationForm onSuccess={closeDialog} onCancel={closeDialog} />
      </CDialog>
    </VFlex>
  );
};

export default HealthCareOrganizations;
